/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Modal } from '../modals/Modal';
import { ScrollView, Text, useWindowDimensions, View } from 'react-native';

import type { CatalogCsvEntityKind } from '../csv-core/public-api';
import type { CsvFlowAdapter } from './hooks/csvFlowAdapter';
import type { CsvImportExportStep } from './hooks/useCsvImportExportFlow';
import type { CsvFlowMode } from '../csv-core/domain/csvUiCopy';
import {
  getCsvExportButtonLabel,
  getCsvTemplateButtonLabel,
  shouldOfferCsvTemplate,
} from '../csv-core/domain/csvUiCopy';
import { useModalBackdropPadding } from '../ui/hooks-ui/useModalBackdropPadding';
import { modalShellShared } from '../modals/modalShellShared.styles';
import { ModalScreenHeader, modalExperience } from '../modals/public-api';
import { TicketModalHeader } from '../ticket/public-api';
import { posSharedUi } from '../../shared/ui/posSharedUi';
import { CsvImportDoneStep } from './CsvImportDoneStep';
import { CsvImportGuideStep } from './CsvImportGuideStep';
import { CsvImportPreviewActions, CsvImportPreviewStep } from './CsvImportPreviewStep';
import { csvImportExportModalStyles as styles } from './csvImportExportModal.styles';
import { COMPACT_PREVIEW_BREAKPOINT, WIDE_BREAKPOINT } from './hooks/csvPreviewHelpers';
import { useCsvImportExportModal } from './hooks/useCsvImportExportFlow';

type Props = {
  visible: boolean;
  onClose: () => void;
  mode: CsvFlowMode;
  catalogEntity?: CatalogCsvEntityKind;
  /** Kicker del header nuevo estilo (ej. "Catálogo"). */
  kicker?: string;
  branchId?: string | null;
  branchLabel?: string;
  canEdit: boolean;
  maxProducts?: number | null;
  performedByUserId?: string | null;
  onApplied?: () => void | Promise<void>;
  adapter: CsvFlowAdapter;
  /** Segundo alcance de export en el paso guía (ej. listado filtrado de Stock). */
  filteredExportLabel?: string;
  onDownloadFilteredExport?: () => void;
};

export const CsvImportExportModal = ({
  visible,
  onClose,
  mode,
  catalogEntity = 'products',
  kicker,
  branchId = null,
  branchLabel,
  canEdit,
  maxProducts = null,
  performedByUserId = null,
  onApplied,
  adapter,
  filteredExportLabel,
  onDownloadFilteredExport,
}: Props) => {
  const { width } = useWindowDimensions();
  const isWide = width >= WIDE_BREAKPOINT;
  const isCompactPreview = width < COMPACT_PREVIEW_BREAKPOINT;
  const backdropPadding = useModalBackdropPadding();

  const modalState = useCsvImportExportModal({
    visible,
    mode,
    catalogEntity,
    branchId,
    branchLabel,
    maxProducts,
    performedByUserId,
    onApplied,
    adapter,
  });

  const { flow } = modalState;

  const handleClose = () => {
    // No resetear el flujo acá: si step vuelve a "guide" mientras visible sigue true,
    // el usuario ve un flash del wizard antes de que el padre cierre el modal.
    onClose();
  };

  const handleImportAnother = () => {
    flow.reset();
    modalState.setPreviewErrorsOnly(false);
    modalState.setPreviewWarningsOnly(false);
    modalState.setShowEqualRows(false);
    modalState.setPreviewPageIndex(0);
    modalState.setColumnsExpanded(false);
  };

  const bodyByStep: Record<CsvImportExportStep, React.ReactNode> = {
    guide: (
      <CsvImportGuideStep
        guideNote={modalState.guideNote}
        mode={mode}
        branchLabel={branchLabel}
        maxProducts={maxProducts}
        canEdit={canEdit}
        loading={flow.loading}
        columnsExpanded={modalState.columnsExpanded}
        columnHints={modalState.columnHints}
        showTemplate={shouldOfferCsvTemplate(mode, catalogEntity)}
        templateLabel={getCsvTemplateButtonLabel(mode)}
        exportLabel={getCsvExportButtonLabel(mode, catalogEntity)}
        onToggleColumnsExpanded={() => modalState.setColumnsExpanded((v) => !v)}
        onDownloadTemplate={() => void flow.downloadTemplate()}
        onDownloadExport={() => void flow.downloadExport()}
        filteredExportLabel={filteredExportLabel}
        onDownloadFilteredExport={onDownloadFilteredExport}
        onPickAndPreview={() => void flow.pickAndPreview()}
      />
    ),
    preview: (
      <CsvImportPreviewStep
        fileName={flow.fileName}
        previewStats={flow.preview}
        previewCounts={modalState.previewCounts}
        canConfirmImport={flow.canConfirmImport}
        previewErrorsOnly={modalState.previewErrorsOnly}
        previewWarningsOnly={modalState.previewWarningsOnly}
        showEqualRows={modalState.showEqualRows}
        previewTotalCount={modalState.previewTotalCount}
        previewPageIndex={modalState.previewPageIndex}
        previewPageSize={modalState.previewPageSize}
        pagedPreviewRows={modalState.pagedPreviewRows}
        isCompactPreview={isCompactPreview}
        onTogglePreviewErrorsOnly={() => {
          modalState.setPreviewErrorsOnly((v) => !v);
          modalState.setPreviewWarningsOnly(false);
          modalState.setShowEqualRows(false);
        }}
        onTogglePreviewWarningsOnly={() => {
          modalState.setPreviewWarningsOnly((v) => !v);
          modalState.setPreviewErrorsOnly(false);
          modalState.setShowEqualRows(false);
        }}
        onToggleShowEqualRows={() => modalState.setShowEqualRows((v) => !v)}
        onPageSizeChange={(size) => {
          modalState.setPreviewPageSize(size);
          modalState.setPreviewPageIndex(0);
        }}
        onPreviousPage={() => modalState.setPreviewPageIndex((p) => Math.max(0, p - 1))}
        onNextPage={() => modalState.setPreviewPageIndex((p) => p + 1)}
      />
    ),
    done: (
      <CsvImportDoneStep
        counts={
          flow.lastDoneCounts ?? {
            applied: 0,
            skipped: 0,
            failed: flow.lastErrors.length,
            altas: 0,
            ediciones: 0,
          }
        }
        mode={mode}
        errorCount={flow.lastErrors.length}
        onDownloadErrorReport={() => void flow.downloadErrorReport()}
        onImportAnother={handleImportAnother}
        onClose={handleClose}
      />
    ),
  };

  return (
    <Modal visible={visible} animationType="fade" transparent onRequestClose={handleClose}>
      <View style={[posSharedUi.modalBackdrop, modalExperience.modalRoot, backdropPadding]}>
        <View
          style={[
            posSharedUi.modalCard,
            styles.modalCard,
            modalShellShared.modalCardShell,
            isWide ? styles.modalCardWide : modalShellShared.modalMaxCompact,
            kicker ? styles.modalCardFlush : null,
          ]}
        >
          {kicker ? (
            <TicketModalHeader
              kicker={kicker}
              title={`CSV · ${flow.entityLabel}`}
              subtitle={flow.step !== 'done' ? modalState.heroSubtitle : undefined}
              onClose={handleClose}
              compact={!isWide}
            />
          ) : (
            <>
              <ModalScreenHeader title={`CSV · ${flow.entityLabel}`} onClose={handleClose} />
              {flow.step !== 'done' ? (
                <Text style={styles.subtitle}>{modalState.heroSubtitle}</Text>
              ) : null}
            </>
          )}
          <ScrollView
            style={styles.scrollContent}
            contentContainerStyle={styles.scrollContentInner}
            keyboardShouldPersistTaps="handled"
          >
            {bodyByStep[flow.step]}
          </ScrollView>
          {flow.step === 'preview' ? (
            <CsvImportPreviewActions
              isCompactPreview={isCompactPreview}
              committing={flow.committing}
              canConfirmImport={flow.canConfirmImport}
              onBack={() => flow.setStep('guide')}
              onConfirmImport={() => void flow.confirmImport()}
            />
          ) : null}
        </View>
      </View>
    </Modal>
  );
};
